/**
 * Provider-aware reservation eligibility for marketplace order import.
 *
 * Only sellable committed quantities reserve inventory. Import creates the
 * sales order with qty_allocated = 0; reservation is a separate allocation
 * ledger write (on_hand unchanged). Replay of an already-imported order must
 * not reserve again — that is handled by allocate remaining-qty = 0.
 */

export type MarketplaceReservationInput = {
  provider?: string | null;
  paymentStatus?: string | null;
  orderStatus?: string | null;
  fulfillmentStatus?: string | null;
};

const PAID = new Set(["paid", "captured", "completed", "paid_off_platform"]);

const BLOCKED_PAYMENT = new Set([
  "pending",
  "unpaid",
  "failed",
  "declined",
  "void",
  "voided",
  "cancelled",
  "canceled",
  "refunded",
  "partially_refunded",
  "authorized",
]);

const BLOCKED_ORDER = new Set([
  "cancelled",
  "canceled",
  "declined",
  "refunded",
  "incomplete",
  "pending",
  "awaiting payment",
  "awaiting_payment",
]);

const BLOCKED_BC_STATUS = new Set(["completed", "shipped"]);

const BLOCKED_FULFILMENT = new Set([
  "fulfilled",
  "completed",
  "shipped",
  "fully_shipped",
  "fully shipped",
]);

function norm(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase().replace(/_/g, " ");
}

export function marketplaceOrderShouldReserve(input: MarketplaceReservationInput): boolean {
  const payment = norm(input.paymentStatus);
  if (!payment || BLOCKED_PAYMENT.has(payment) || BLOCKED_PAYMENT.has(payment.replace(/ /g, "_"))) {
    return false;
  }
  if (!PAID.has(payment.replace(/ /g, "_")) && payment !== "paid") {
    return false;
  }

  const orderStatus = norm(input.orderStatus);
  if (orderStatus && (BLOCKED_ORDER.has(orderStatus) || BLOCKED_ORDER.has(orderStatus.replace(/ /g, "_")))) {
    return false;
  }
  const provider = norm(input.provider).replace(/ /g, "");
  if (provider === "bigcommerce" && orderStatus && BLOCKED_BC_STATUS.has(orderStatus)) {
    return false;
  }

  const fulfilment = norm(input.fulfillmentStatus);
  if (fulfilment && (BLOCKED_FULFILMENT.has(fulfilment) || fulfilment === "fulfilled")) {
    return false;
  }

  return true;
}

export function sellableStockLabel(opts: {
  available: number;
  onHand: number;
  allocated: number;
}): { primary: string; secondary: string } {
  return {
    primary: `${opts.available} available`,
    secondary: `${opts.onHand} on hand · ${opts.allocated} allocated`,
  };
}
